import { replicateCouchDB } from "rxdb/plugins/replication-couchdb"
import type { RxCollection, WithDeleted } from "rxdb"
import { API_COUCH_URI } from "../../config"
import type { FetchCouch } from "../../http/useFetchCouch"
/**
 * Strips legacy `resourceId` / `linkResourceId` fields from deleted
 * documents so they don't break the local schema.
 *
 * Non-deleted documents are migrated on both the server (admin migration)
 * and the client (RxDB schema migration strategies). Deleted documents
 * however are CouchDB tombstones that persist forever and cannot be
 * updated, so neither migration can clean them up. Since deleted documents
 * are only pulled to propagate the deletion and their field values are
 * never used, we just strip the legacy fields to satisfy the schema.
 *
 * This modifier can be removed once the stale tombstones have been purged
 * from CouchDB via the `_purge` endpoint.
 */
function stripLegacyResourceIdFromDeletedDoc(
  docData: WithDeleted<Record<string, unknown>>,
): WithDeleted<Record<string, unknown>> {
  if (!docData._deleted) return docData

  const { linkResourceId, resourceId, ...rest } = docData

  return rest
}

export const replicateCouchDBCollection = ({
  dbName,
  collection,
  host,
  autoStart = false,
  cancelSignal,
  suffix,
  fetchCouch,
  ...params
}: {
  dbName: string
  collection: RxCollection
  host?: string
  autoStart?: boolean
  cancelSignal: AbortSignal
  suffix?: string
  fetchCouch: FetchCouch
} & Omit<
  Parameters<typeof replicateCouchDB>[0],
  "pull" | "push" | "url" | "replicationIdentifier" | "collection"
>) => {
  const uri = host ?? API_COUCH_URI

  return replicateCouchDB({
    replicationIdentifier: `${uri}/${dbName}-${collection.name}${suffix ? `-${suffix}` : ""}-replication`,
    collection: collection,
    url: `${uri}/${dbName}/`,
    live: false,
    waitForLeadership: false,
    autoStart,
    ...params,
    fetch: async (url, options) => {
      // flat clone the given options to not mutate the input
      const optionsWithAuth = Object.assign({}, options)
      // ensure the headers property exists
      if (!optionsWithAuth.headers) {
        optionsWithAuth.headers = {}
      }

      if (
        typeof url === "string" &&
        url.startsWith(`${uri}/${dbName}/_changes`)
      ) {
        const { response } = await fetchCouch({
          input: `${url}&filter=_selector`,
          config: {
            ...optionsWithAuth,
            method: "post",
            headers: {
              ...optionsWithAuth.headers,
              "Content-Type": "application/json",
            },
            unwrap: false,
            signal: cancelSignal,
            body: JSON.stringify({ selector: { rx_model: collection.name } }),
          },
        })

        return response
      }

      // call the original fetch function with our custom options.
      const { response } = await fetchCouch({
        input: url,
        config: {
          ...optionsWithAuth,
          signal: cancelSignal,
          unwrap: false,
        },
      })

      return response
    },
    pull: {
      /**
       * Amount of documents to be fetched in one HTTP request
       * (optional)
       */
      batchSize: 60,
      /**
       * Custom modifier to mutate pulled documents
       * before storing them in RxDB.
       * (optional)
       */
      modifier: (docData) => {
        // @todo move somewhere else
        if ("rx_model" in docData && docData.rx_model === "tag") {
          // old property not used anymore
          if ("isHidden" in docData) {
            delete docData.isHidden
          }
        }

        if ("rx_model" in docData && docData.rx_model === "link") {
          if ("data" in docData && typeof docData.data === "string") {
            try {
              docData.data = JSON.parse(docData.data)
            } catch (_error) {
              docData.data = {}
            }
          }
        }

        if ("rx_model" in docData && docData.rx_model === "obokucollection") {
          if ("linkData" in docData && typeof docData.linkData === "string") {
            try {
              docData.linkData = JSON.parse(docData.linkData)
            } catch (_error) {
              docData.linkData = {}
            }
          }
        }

        return stripLegacyResourceIdFromDeletedDoc(docData)
      },
    },
    push: {
      /**
       * How many local changes to process at once.
       * (optional)
       */
      batchSize: 60,
      /**
       * Custom modifier to mutate documents
       * before sending them to the CouchDB endpoint.
       * (optional)
       */
      modifier: (docData) => {
        return stripLegacyResourceIdFromDeletedDoc(docData)
      },
    },
  })
}
